import { useMemo } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import ContentLibraryTable from "components/content_library/ContentLibraryTable";
import { buildContentLibraryPath } from "components/content_library/utils";
import { ProviderType } from "components/integrations/managed_integrations/types";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  SimpleBreadcrumb,
} from "components/ui/breadcrumb/SimpleBreadcrumb";
import { CustomerDocumentReviewStatus } from "lib/types/graphQLEnums";

const getRootTitle = (provider?: ProviderType | "uploads") => {
  switch (provider) {
    case "googledrive":
      return "Google Drive";
    case "sharepoint":
      return "Microsoft SharePoint";
  }
  return "Content library";
};

export function ToReviewContentLibrary(): JSX.Element {
  return (
    <Page>
      <Content inFolder={false}>
        <Flex column gap={24}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text h1>To review</Text>
          </Flex>
          <ContentLibraryTable
            reviewStatus={[
              CustomerDocumentReviewStatus.Warning,
              CustomerDocumentReviewStatus.Overdue,
            ]}
            hideActionAndDescription
          />
        </Flex>
      </Content>
    </Page>
  );
}

export function UnassignedContentLibrary(): JSX.Element {
  return (
    <Page>
      <Content inFolder={false}>
        <Flex column gap={24}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text h1>Unassigned</Text>
          </Flex>
          <ContentLibraryTable
            initialOwnerFilter={{ ids: [], includeNulls: true }}
            hideActionAndDescription
          />
        </Flex>
      </Content>
    </Page>
  );
}

type Props = {
  provider: ProviderType | "uploads";
  folderPath?: string;
};
export function ContentLibrary({ provider, folderPath }: Props): JSX.Element {
  const navigate = useNavigate();
  const segments = useMemo(() => folderPath?.split("/") ?? [], [folderPath]);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    if (segments.length === 0) return [];

    const items: BreadcrumbItem[] = [
      { label: getRootTitle(provider), path: buildContentLibraryPath(provider) },
    ];

    segments.forEach((segment, index) => {
      const isLast = index === segments.length - 1;
      items.push({
        label: segment,
        path: isLast ? undefined : buildContentLibraryPath(provider, segments.slice(0, index + 1)),
      });
    });

    return items;
  }, [segments, provider]);

  return (
    <Page>
      {breadcrumbItems.length > 0 && (
        <BreadcrumbContainer>
          <SimpleBreadcrumb items={breadcrumbItems} onNavigate={(path) => navigate({ to: path })} />
        </BreadcrumbContainer>
      )}
      <Content inFolder={!!folderPath}>
        <Flex column gap={24}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text h1>{segments[segments.length - 1] ?? getRootTitle(provider)}</Text>
          </Flex>
          <ContentLibraryTable key={provider} provider={provider} folderPath={folderPath} />
        </Flex>
      </Content>
    </Page>
  );
}

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 40,
  overflow: "auto",
  height: "100%",
  width: "100%",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const Content = styled(Flex)<{
  inFolder: boolean;
}>(({ inFolder }) => ({
  flexDirection: "column",
  gap: 40,
  padding: 24,
  paddingTop: inFolder ? 0 : 24,
}));
